import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  TextInput,
  Textarea,
  Select,
  Alert,
  Spinner,
  Badge,
  Pagination,
} from 'flowbite-react';
import Select2 from 'react-select';
import { fetchWithAuth } from '../utils/api';
import { getCampaignsEndpoints, getPagesEndpoints } from '../config/api';
import {
  getValidationMessages,
  formatCurrency,
  formatDate,
} from '../utils/helper';
import { loadMarketersList, loadPagesList } from '../config/lib';

interface Campaign {
  id: number;
  name: string;
  tag_name: string;
  message_template: string[];
  ref: string | null;
  post_id: string | null;
  ads_ids: string[];
  page_id: string | null;
  page_name: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  note: string | null;
  marketer: number;
  marketer_name?: string;
  total_lead: number;
  total_lead_today: number;
  total_ql: number;
  revenue: string;
  revenue_today: string;
  campaign_type: (typeof CAMPAIGN_TYPES)[keyof typeof CAMPAIGN_TYPES];
  source: (typeof SOURCE_TYPES)[keyof typeof SOURCE_TYPES];
  created_at: string;
  updated_at: string;
}

const TAG_NAME_OPTIONS: string[] = ['App', 'Off', 'Sách', 'Kaiwa', 'Zoom'];

interface Marketer {
  id: number;
  name: string;
}

interface PageItemOption {
  id: number;
  name: string;
  page_id: string;
  source_type: string;
}

interface CampaignFormData {
  name: string;
  message_template: string[];
  tag_name: string;
  ref: string;
  post_id: string;
  ads_ids: string[];
  page_id: string;
  note: string;
  marketer: number;
  utm_campaign: string;
  utm_term: string;
  campaign_type: (typeof CAMPAIGN_TYPES)[keyof typeof CAMPAIGN_TYPES];
  source: (typeof SOURCE_TYPES)[keyof typeof SOURCE_TYPES];
}

export const CAMPAIGN_TYPES = {
  NEW: 'new',
  OLD: 'old',
} as const;

export const SOURCE_TYPES = {
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
  TIKTOK: 'tiktok',
} as const;

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [marketers, setMarketers] = useState<Marketer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);
  const [messagePopup, setMessagePopup] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  // Pages select data
  const [pages, setPages] = useState<PageItemOption[]>([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [marketerFilter, setMarketerFilter] = useState<{
    value: number;
    label: string;
  } | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  // Form states
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    message_template: [],
    tag_name: '',
    ref: '',
    post_id: '',
    ads_ids: [],
    page_id: '',
    utm_campaign: '',
    utm_term: '',
    note: '',
    marketer: 0,
    campaign_type: CAMPAIGN_TYPES.NEW,
    source: SOURCE_TYPES.FACEBOOK,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load campaigns
  const loadCampaigns = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const campaignsEndpoint = getCampaignsEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(marketerFilter && { marketer: marketerFilter.value.toString() }),
      });

      const response = await fetchWithAuth(`${campaignsEndpoint}?${params}`);

      if (response.success && response.data) {
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
        setIsLoadingAnalytics(true);
        loadAnalytics(
          response.data.results.map((campaign: Campaign) => campaign.id)
        )
          .then(analytics => {
            if (analytics && analytics.leads && analytics.orders) {
              for (const campaign of response.data.results) {
                const analyticsLeads = analytics.leads.find(
                  (a: any) => a.campaign_id === campaign.id
                );
                const analyticsRevenue = analytics.orders.find(
                  (a: any) => a.campaign_id === campaign.id
                );
                campaign.total_lead_today = analyticsLeads?.count || 0;
                campaign.revenue_today = analyticsRevenue?.total || 0;
              }
              setCampaigns(response.data.results);
            }
          })
          .finally(() => {
            setIsLoadingAnalytics(false);
          });
        setCampaigns(response.data.results);
      } else {
        setError('Không thể tải danh sách campaign');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load campaigns error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async (campaignIds: string[]) => {
    const analyticsEndpoint = getCampaignsEndpoints().ANALYTICS;
    const params = new URLSearchParams();
    campaignIds.forEach(id => params.append('campaign_ids', id));
    const response = await fetchWithAuth(
      `${analyticsEndpoint}?${params.toString()}`
    );
    if (response.success && response.data) {
      return response.data || [];
    }
    return [];
  };

  // Load marketers
  const loadMarketers = async () => {
    const marketersList = await loadMarketersList();
    setMarketers(marketersList);
  };

  // Load pages for select
  const loadPages = async () => {
    const pagesList = await loadPagesList();
    setPages(pagesList);
  };

  useEffect(() => {
    loadCampaigns();
    loadMarketers();
    loadPages();
  }, []);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    loadCampaigns(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadCampaigns(page);
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setEditingCampaign(null);
    setFormData({
      name: '',
      message_template: [],
      tag_name: '',
      ref: '',
      post_id: '',
      page_id: '',
      ads_ids: [],
      utm_campaign: '',
      utm_term: '',
      note: '',
      marketer: 0,
      campaign_type: CAMPAIGN_TYPES.NEW,
      source: SOURCE_TYPES.FACEBOOK,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Open edit modal
  const openEditModal = (campaign: Campaign) => {
    setModalMode('edit');
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      message_template:
        campaign.message_template.length > 0 ? campaign.message_template : [],
      tag_name:
        campaign.tag_name && campaign.tag_name.length > 0
          ? campaign.tag_name
          : '',
      ref: campaign.ref || '',
      post_id: campaign.post_id || '',
      ads_ids:
        campaign.ads_ids && campaign.ads_ids.length > 0 ? campaign.ads_ids : [],
      page_id: campaign.page_id || '',
      note: campaign.note || '',
      utm_campaign: campaign.utm_campaign || '',
      utm_term: campaign.utm_term || '',
      marketer: campaign.marketer,
      campaign_type: campaign.campaign_type,
      source: campaign.source,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Handle form input change
  const handleInputChange = (field: keyof CampaignFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle message template change
  const handleMessageTemplateChange = (index: number, value: string) => {
    const newTemplates = [...formData.message_template];
    newTemplates[index] = value;
    setFormData(prev => ({ ...prev, message_template: newTemplates }));
  };

  // Add message template
  const addMessageTemplate = () => {
    setFormData(prev => ({
      ...prev,
      message_template: [...prev.message_template, ''],
    }));
  };

  // Remove message template
  const removeMessageTemplate = (index: number) => {
    if (formData.message_template.length > 0) {
      const newTemplates = formData.message_template.filter(
        (_, i) => i !== index
      );
      setFormData(prev => ({ ...prev, message_template: newTemplates }));
    }
  };

  // Handle ads ID change
  const handleAdsIdChange = (index: number, value: string) => {
    const newAdsIds = [...formData.ads_ids];
    newAdsIds[index] = value;
    setFormData(prev => ({ ...prev, ads_ids: newAdsIds }));
    if (formErrors.ads_ids) {
      setFormErrors(prev => ({ ...prev, ads_ids: '' }));
    }
  };

  // Add ads ID
  const addAdsId = () => {
    setFormData(prev => ({
      ...prev,
      ads_ids: [...prev.ads_ids, ''],
    }));
  };

  // Remove ads ID
  const removeAdsId = (index: number) => {
    if (formData.ads_ids.length > 0) {
      const newAdsIds = formData.ads_ids.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, ads_ids: newAdsIds }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Tên campaign là bắt buộc';
    }

    if (!formData.marketer || formData.marketer === 0) {
      errors.marketer = 'Marketer là bắt buộc';
    }

    if (formData.source === SOURCE_TYPES.GOOGLE) {
      if (!formData.utm_campaign.trim()) {
        errors.utm_campaign = 'UTM Campaign là bắt buộc';
      }
      if (!formData.utm_term.trim()) {
        errors.utm_term = 'UTM Term là bắt buộc';
      }
    }
    if (formData.source === SOURCE_TYPES.FACEBOOK) {
      if (
        formData.ads_ids.length === 0 ||
        formData.ads_ids.some(adsId => !adsId.trim())
      ) {
        errors.ads_ids =
          'Cần ít nhất một Ads ID và tất cả Ads ID phải có nội dung';
      }

      if (!formData.page_id.trim()) {
        errors.page_id = 'Page ID là bắt buộc';
      }

      if (formData.message_template.some(template => !template.trim())) {
        errors.message_template = 'Tất cả message template phải có nội dung';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const campaignsEndpoint = getCampaignsEndpoints();
      const endpoint =
        modalMode === 'create'
          ? campaignsEndpoint.CREATE
          : `${campaignsEndpoint.UPDATE}/${editingCampaign?.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify({
          ...formData,
          ref: formData.ref || null,
          post_id: formData.post_id || null,
          ads_ids: formData.ads_ids.length > 0 ? formData.ads_ids : [],
          tag_name: formData.tag_name || null,
          page_id: formData.page_id || null,
          note: formData.note || null,
        }),
      });

      if (response.success) {
        setMessagePopup({
          type: 'success',
          text: `Campaign ${
            modalMode === 'create' ? 'đã được tạo' : 'đã được cập nhật'
          } thành công!`,
        });
        setShowModal(false);
        loadCampaigns(currentPage);
      } else {
        setMessagePopup({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            `Không thể ${modalMode === 'create' ? 'tạo' : 'cập nhật'} campaign`,
        });
      }
    } catch (error) {
      setMessagePopup({
        type: 'failure',
        text: `Đã xảy ra lỗi khi ${
          modalMode === 'create' ? 'tạo' : 'cập nhật'
        } campaign`,
      });
      console.error('Submit campaign error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (campaignId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa campaign này?')) {
      return;
    }

    try {
      const campaignsEndpoint = getCampaignsEndpoints();
      const response = await fetchWithAuth(
        `${campaignsEndpoint.DELETE}/${campaignId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Campaign đã được xóa thành công!',
        });
        loadCampaigns(currentPage);
      } else {
        setMessage({
          type: 'failure',
          text: 'Không thể xóa campaign',
        });
      }
    } catch (error) {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi xóa campaign',
      });
      console.error('Delete campaign error:', error);
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải danh sách campaign...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Campaigns</h1>
            <p className="text-gray-600">Quản lý các chiến dịch quảng cáo</p>
          </div>
          <Button onClick={openCreateModal} className="btn-primary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Tạo Campaign
          </Button>
        </div>
      </div>

      {message && (
        <Alert color={message.type} className="mb-4">
          <span>{message.text}</span>
        </Alert>
      )}

      {/* Search and Filter */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <TextInput
              placeholder="Tìm kiếm theo tên campaign..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Select2
              value={marketerFilter}
              onChange={e => setMarketerFilter(e)}
              options={marketers.map(marketer => ({
                value: marketer.id,
                label: marketer.name,
              }))}
              isClearable
              placeholder="Chọn Marketer..."
            />
            <Button onClick={handleSearch} className="btn-primary">
              Tìm kiếm
            </Button>
          </div>
        </div>
      </Card>

      {/* Campaigns Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>ID</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
              <Table.HeadCell>Tên Campaign</Table.HeadCell>
              <Table.HeadCell>Tag</Table.HeadCell>
              <Table.HeadCell>Marketer</Table.HeadCell>
              <Table.HeadCell>Tên Trang</Table.HeadCell>
              <Table.HeadCell>Tổng LEAD</Table.HeadCell>
              <Table.HeadCell>LEAD Hôm nay</Table.HeadCell>
              <Table.HeadCell>Doanh thu</Table.HeadCell>
              <Table.HeadCell>Doanh thu hôm nay</Table.HeadCell>
              <Table.HeadCell>Nguồn</Table.HeadCell>
              <Table.HeadCell>Ngày tạo</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {campaigns.map(campaign => (
                <Table.Row
                  key={campaign.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {campaign.id}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => openEditModal(campaign)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="sm"
                        color="red"
                        onClick={() => handleDelete(campaign.id.toString())}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="font-medium text-gray-900 dark:text-white">
                    {campaign.name}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="red">
                      {campaign.tag_name?.toUpperCase() || '-'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color="blue">
                      {campaign.marketer_name ||
                        `Marketer ${campaign.marketer}`}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="blue">{campaign.page_name || '-'}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="green">{campaign.total_lead}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="green">
                      {isLoadingAnalytics ? (
                        <Spinner size="sm" />
                      ) : (
                        campaign.total_lead_today ?? '-'
                      )}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="yellow">
                      {formatCurrency(parseFloat(campaign.revenue))}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="yellow">
                      {isLoadingAnalytics ? (
                        <Spinner size="sm" />
                      ) : (
                        formatCurrency(
                          parseFloat(campaign.revenue_today ?? '0')
                        )
                      )}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge
                      color={
                        campaign.source === SOURCE_TYPES.FACEBOOK
                          ? 'blue'
                          : campaign.source === SOURCE_TYPES.GOOGLE
                          ? 'red'
                          : campaign.source === SOURCE_TYPES.TIKTOK
                          ? 'purple'
                          : 'black'
                      }
                    >
                      {campaign.source.toUpperCase() || '-'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(campaign.created_at)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4">
            <div className="text-sm text-gray-700">
              Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
              {totalItems} kết quả
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="4xl">
        <Modal.Header>
          {modalMode === 'create' ? 'Tạo Campaign Mới' : 'Chỉnh Sửa Campaign'}
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {messagePopup && (
              <Alert color={messagePopup.type} className="mb-4">
                <span>{messagePopup.text}</span>
              </Alert>
            )}
            <div className="space-y-4">
              {/* Campaign Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên Campaign *
                </label>
                <TextInput
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  placeholder="Nhập tên campaign"
                  color={formErrors.name ? 'failure' : undefined}
                  helperText={formErrors.name}
                />
              </div>

              {/* Marketer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marketer *
                </label>

                <Select2
                  value={
                    formData.marketer
                      ? {
                          value: formData.marketer,
                          label:
                            marketers.find(
                              marketer => marketer.id === formData.marketer
                            )?.name || '',
                        }
                      : null
                  }
                  onChange={e =>
                    handleInputChange(
                      'marketer',
                      parseInt(e?.value?.toString() || '0')
                    )
                  }
                  options={marketers.map(marketer => ({
                    value: marketer.id,
                    label: marketer.name,
                  }))}
                  isClearable
                  placeholder="Chọn Marketer..."
                />

                {formErrors.marketer && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.marketer}
                  </p>
                )}
              </div>

              {/* Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source
                </label>
                <Select
                  value={formData.source}
                  onChange={e => handleInputChange('source', e.target.value)}
                >
                  <option value={SOURCE_TYPES.FACEBOOK}>Facebook</option>
                  <option value={SOURCE_TYPES.GOOGLE}>Google</option>
                  <option value={SOURCE_TYPES.TIKTOK}>TikTok</option>
                </Select>
              </div>

              {/* Products - hiển thị cho mọi source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên Tag
                </label>
                <Select
                  value={formData.tag_name}
                  onChange={e => handleInputChange('tag_name', e.target.value)}
                >
                  <option value="">Chọn Tag</option>
                  {TAG_NAME_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Message Templates, Link Tag, Post ID, Ads IDs, Page: chỉ hiển thị khi source là facebook */}
              {formData.source === SOURCE_TYPES.FACEBOOK && (
                <>
                  {/* Message Templates */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mẫu tin nhắn
                    </label>
                    {formData.message_template.map((template, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        <TextInput
                          value={template}
                          onChange={e =>
                            handleMessageTemplateChange(index, e.target.value)
                          }
                          placeholder={`Message template ${index + 1}`}
                          className="flex-1"
                        />
                        {formData.message_template.length > 0 && (
                          <Button
                            type="button"
                            color="red"
                            size="sm"
                            onClick={() => removeMessageTemplate(index)}
                          >
                            Xóa
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      color="blue"
                      size="sm"
                      onClick={addMessageTemplate}
                      className="mt-2"
                    >
                      Thêm mẫu tin nhắn
                    </Button>
                    {formErrors.message_template && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.message_template}
                      </p>
                    )}
                  </div>

                  {/* Link Tag and IDs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Link Tag
                      </label>
                      <TextInput
                        value={formData.ref}
                        onChange={e => handleInputChange('ref', e.target.value)}
                        placeholder="Nhập ref"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Post ID
                      </label>
                      <TextInput
                        value={formData.post_id}
                        onChange={e =>
                          handleInputChange('post_id', e.target.value)
                        }
                        placeholder="Nhập Post ID"
                      />
                    </div>
                  </div>

                  {/* Ads IDs */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ads IDs *
                    </label>
                    {formData.ads_ids.map((adsId, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        <TextInput
                          value={adsId}
                          onChange={e =>
                            handleAdsIdChange(index, e.target.value)
                          }
                          placeholder={`Ads ID ${index + 1}`}
                          className="flex-1"
                          color={formErrors.ads_ids ? 'failure' : undefined}
                        />
                        {formData.ads_ids.length > 0 && (
                          <Button
                            type="button"
                            color="red"
                            size="sm"
                            onClick={() => removeAdsId(index)}
                          >
                            Xóa
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      color="blue"
                      size="sm"
                      onClick={addAdsId}
                      className="mt-2"
                    >
                      Thêm Ads ID
                    </Button>
                    {formErrors.ads_ids && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.ads_ids}
                      </p>
                    )}
                  </div>

                  {/* Page */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Page *
                    </label>
                    <Select
                      value={formData.page_id}
                      onChange={e =>
                        handleInputChange('page_id', e.target.value)
                      }
                      color={formErrors.page_id ? 'failure' : undefined}
                    >
                      <option value="">Chọn Page</option>
                      {pages
                        .filter(p => p.source_type === SOURCE_TYPES.FACEBOOK)
                        .map(p => (
                          <option key={p.id} value={p.page_id || ''}>
                            {p.name} {p.page_id ? `(#${p.page_id})` : ''}
                          </option>
                        ))}
                    </Select>
                    {formErrors.page_id && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.page_id}
                      </p>
                    )}
                  </div>
                </>
              )}
              {formData.source === SOURCE_TYPES.TIKTOK && (
                <>
                  {/* Page */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Page *
                    </label>
                    <Select
                      value={formData.page_id}
                      onChange={e =>
                        handleInputChange('page_id', e.target.value)
                      }
                      color={formErrors.page_id ? 'failure' : undefined}
                    >
                      <option value="">Chọn Page</option>
                      {pages
                        .filter(p => p.source_type === SOURCE_TYPES.TIKTOK)
                        .map(p => (
                          <option key={p.id} value={p.page_id || ''}>
                            {p.name} {p.page_id ? `(#${p.page_id})` : ''}
                          </option>
                        ))}
                    </Select>
                    {formErrors.page_id && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.page_id}
                      </p>
                    )}
                  </div>
                  {/* Ads IDs */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ads IDs *
                    </label>
                    {formData.ads_ids.map((adsId, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        <TextInput
                          value={adsId}
                          onChange={e =>
                            handleAdsIdChange(index, e.target.value)
                          }
                          placeholder={`Ads ID ${index + 1}`}
                          className="flex-1"
                          color={formErrors.ads_ids ? 'failure' : undefined}
                        />
                        {formData.ads_ids.length > 0 && (
                          <Button
                            type="button"
                            color="red"
                            size="sm"
                            onClick={() => removeAdsId(index)}
                          >
                            Xóa
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      color="blue"
                      size="sm"
                      onClick={addAdsId}
                      className="mt-2"
                    >
                      Thêm Ads ID
                    </Button>
                    {formErrors.ads_ids && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.ads_ids}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Post ID *
                    </label>
                    <TextInput
                      value={formData.post_id}
                      onChange={e =>
                        handleInputChange('post_id', e.target.value)
                      }
                      placeholder="Nhập Post ID"
                    />
                  </div>
                </>
              )}

              {formData.source === SOURCE_TYPES.GOOGLE && (
                <>
                  {/* UTM Campaign */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      UTM Campaign *
                    </label>
                    <TextInput
                      value={formData.utm_campaign || ''}
                      onChange={e =>
                        handleInputChange('utm_campaign', e.target.value)
                      }
                      placeholder="Nhập UTM Campaign"
                      color={formErrors.utm_campaign ? 'failure' : undefined}
                      helperText={formErrors.utm_campaign}
                    />
                  </div>

                  {/* UTM Term */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      UTM Term *
                    </label>
                    <TextInput
                      value={formData.utm_term || ''}
                      onChange={e =>
                        handleInputChange('utm_term', e.target.value)
                      }
                      placeholder="Nhập UTM Term"
                      color={formErrors.utm_term ? 'failure' : undefined}
                      helperText={formErrors.utm_term}
                    />
                  </div>
                </>
              )}

              {/* Campaign Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Type
                </label>
                <Select
                  value={formData.campaign_type}
                  onChange={e =>
                    handleInputChange('campaign_type', e.target.value)
                  }
                >
                  <option value={CAMPAIGN_TYPES.NEW}>New</option>
                  <option value={CAMPAIGN_TYPES.OLD}>Old</option>
                </Select>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ghi chú
                </label>
                <Textarea
                  value={formData.note}
                  onChange={e => handleInputChange('note', e.target.value)}
                  placeholder="Nhập ghi chú..."
                  rows={3}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="gray"
              onClick={() => setShowModal(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Đang xử lý...
                </>
              ) : modalMode === 'create' ? (
                'Tạo Campaign'
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default Campaigns;
